import { parseEvents } from "./events";
import { buildRoadIndex } from "./roadIndex";
import { matchEvent } from "./matcher";
import { buildArrow } from "./arrows";

const round = (value, digits = 1) => (Number.isFinite(value) ? Number(value.toFixed(digits)) : null);

/**
 * Match events to roads and build the display GeoJSON. Every event yields an
 * `event` point; events with a usable heading or road match also yield an
 * `arrow` line and a `head` point carrying the arrowhead bearing. All three
 * share the event id so hover state and filters apply to the whole arrow.
 * @param {Object} roads GeoJSON FeatureCollection of road LineStrings
 * @param {String} csvText events CSV
 * @param {Object} [options] matching/arrow tuning (see constants.js)
 * @return {{geojson: Object, stats: Object}}
 */
export function buildSafetyGeoJSON(roads, csvText, options = {}) {
  const events = parseEvents(csvText);
  const index = buildRoadIndex(roads);
  const features = [];
  let matched = 0;

  for (const event of events) {
    const match = matchEvent(event, index, options);
    const arrow = buildArrow(event, match, index, options);
    if (match) {
      matched++;
    }

    const shared = {
      event_id: event.event_id,
      event_type: event.event_type,
      max_g: event.max_g,
      hour: event.hour,
      matched: Boolean(match)
    };
    const location = match ? match.snapped : [event.lon, event.lat];

    features.push({
      type: "Feature",
      id: event.event_id,
      properties: {
        ...shared,
        kind: "event",
        device_id: event.device_id,
        heading: event.heading,
        start_speed_mph: event.start_speed_mph,
        end_speed_mph: event.end_speed_mph,
        duration_s: event.duration_s,
        end_time: event.end_time,
        road_name: match ? match.way.name : null,
        snap_distance_m: match ? round(match.distance) : null,
        arrow_length_m: arrow ? round(arrow.lengthM) : null
      },
      geometry: { type: "Point", coordinates: location }
    });

    if (arrow) {
      features.push({
        type: "Feature",
        id: event.event_id,
        properties: { ...shared, kind: "arrow" },
        geometry: { type: "LineString", coordinates: arrow.coordinates }
      });
      features.push({
        type: "Feature",
        id: event.event_id,
        properties: { ...shared, kind: "head", bearing: round(arrow.headBearing) },
        geometry: { type: "Point", coordinates: arrow.coordinates[arrow.coordinates.length - 1] }
      });
    }
  }

  return {
    geojson: { type: "FeatureCollection", features },
    stats: { total: events.length, matched, unmatched: events.length - matched }
  };
}
